import type { PolicySettings, ReturnsSettings } from '@nucleus-commerce/api-client/admin';
import { currencyDecimals } from '@nucleus-commerce/core';

// Whole amounts drop the cents ("$500"), anything else keeps them ("$499.99"); the currency's own
// fraction digits decide what "whole" means so JPY never shows a decimal.
export function formatThreshold(minorUnits: number, currency: string): string {
  const digits = currencyDecimals(currency);
  const major = minorUnits / 10 ** digits;
  const whole = Number.isInteger(major);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : digits,
    maximumFractionDigits: whole ? 0 : digits,
  }).format(major);
}

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * A copy of the backend's `policyHighlights` (lib/policy-highlights.ts): the bullets the
 * storefront renders on product pages and in the footer, in buying-decision order (cost, wait,
 * safety net, after-care). The backend's wording is the authority; keep both in step.
 */
export function policyHighlights(
  policies: PolicySettings,
  returns: ReturnsSettings,
  currency: string,
): string[] {
  const out: string[] = [];

  if (policies.shipping.freeOverCents !== null) {
    out.push(
      policies.shipping.freeOverCents === 0
        ? 'Free shipping'
        : `Free shipping over ${formatThreshold(policies.shipping.freeOverCents, currency)}`,
    );
  }
  const days = policies.shipping.processingDays;
  if (days) {
    out.push(
      days.min === days.max
        ? `Ships in ${plural(days.max, 'business day')}`
        : `Ships in ${days.min} to ${days.max} business days`,
    );
  }

  if (returns.windowDays > 0) {
    out.push(`${returns.windowDays}-day returns`);
    if (policies.returns.refundType === 'store_credit') out.push('Returns for store credit');
    else if (policies.returns.refundType === 'exchange') out.push('Returns for exchange');
    if (returns.restockingFeePercent > 0) {
      out.push(`Restocking fee ${returns.restockingFeePercent}%`);
    }
  } else {
    out.push('Final sale');
  }

  if (policies.warranty.kind === 'lifetime') out.push('Lifetime warranty');
  else if (policies.warranty.kind === 'limited') {
    out.push(
      policies.warranty.years
        ? `${policies.warranty.years}-year limited warranty`
        : 'Limited warranty',
    );
  }

  const months = policies.resizing.freeWithinMonths;
  if (months !== null) {
    out.push(months === 0 ? 'Free resizing' : `Free resizing within ${plural(months, 'month')}`);
  }

  return out;
}
